<template>
  <Transition name="modal">
    <div v-if="props.show" class="fixed inset-0 z-50 overflow-y-auto py-10">
      <!--
        Fixed, not absolute: the wrapper scrolls, so an absolutely positioned shade stays
        the height of the first screen and leaves the rest of a tall modal unshaded.
      -->
      <div class="shade fixed inset-0 bg-black/50" />
      <DefaultGrid class="relative z-2">
        <div class="col-span-full">
          <div
            ref="modal"
            class="modal mx-auto w-full max-w-2xl space-y-6 overflow-hidden rounded-3xl bg-white p-8 md:space-y-7 md:p-10"
          >
            <div class="flex items-center justify-between gap-x-5">
              <h2 class="type-heading-3" v-text="props.title" />
              <BaseButton class="flex-none" @click="onDismiss">
                <SvgGlyphClose />
              </BaseButton>
            </div>
            <div>
              <slot />
            </div>
          </div>
        </div>
      </DefaultGrid>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import type { BaseModalProps } from './types'

const props = withDefaults(defineProps<BaseModalProps>(), {
  dismissable: true,
  show: false,
})

const emit = defineEmits(['dismiss'])

const onDismiss = () => {
  if (props.dismissable) {
    emit('dismiss')
  }
}

onKeyStroke('Escape', onDismiss)

const modal = ref<HTMLElement | null>(null)
onClickOutside(modal, onDismiss)

defineOptions({
  inheritAttrs: false,
})
</script>

<style scoped>
@reference "#css";

.modal-leave-active {
  @apply transition-opacity duration-300 ease-out;
}

.modal-leave-to {
  @apply opacity-0;
}

.modal-enter-active .modal {
  @apply ease-slide transition-all duration-300;
}

.modal-enter-from .modal {
  @apply translate-y-10 opacity-0;
}

.modal-enter-active .shade {
  @apply transition-opacity duration-300;
}

.modal-enter-from .shade {
  @apply opacity-0;
}
</style>
